import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { DEFAULT_PATH_AFTER_SIGN_IN } from '@apps/config';
import { getLoginSchema } from '@apps/containers/LoginContainer/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { FormRenderer } from '@stack/components';
import {
  useLoginMutation,
  useRequestOtpMutation,
  useSecureLoginMutation,
} from '@stack/data-access';
import { getDeviceId } from '@stack/helpers';
import { redirect, setAuthToken } from '@webbyx/next-js';
import NextLink from 'next/link';
import * as yup from 'yup';

import { LoginFormProps } from './props';

// ======================= VALIDATION
const loginSchema = yup.object().shape({
  user_mobile: yup.string().required('Phone is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
});

const secureLoginSchema = yup.object().shape({
  user_mobile: yup.string().required('Phone is required'),
  otp_no: yup.string().required('OTP is required'),
});

const LoginContainer = (props: LoginFormProps) => {
  const { isOtpLogin, onSetIsOtpLogin } = props;
  // ======================= VARIABLES

  // ======================= STATE
  const [agree, setAgree] = useState(false);

  const [phone, setPhone] = useState<string>('');

  // ======================= HOOKS
  const loginFormRef = useRef<any>(null);

  // ======================= API
  const [loginMutation, { loading: loginLoading }] = useLoginMutation({
    onCompleted: (data) => {
      const token = data.Login?.TokenResponse?.token;
      if (token) {
        // setCookie('refreshToken', refreshToken); // optional, if you have a refresh token
        setAuthToken(token);
        redirect({}, DEFAULT_PATH_AFTER_SIGN_IN, 'replace');
      } else {
        onSetIsOtpLogin(true);
        setAgree(false);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const [secureLoginMutation, { loading: secureLoginLoading }] =
    useSecureLoginMutation({
      onCompleted: (data) => {
        const token = data.SecureLogin?.TokenResponse?.token;
        if (token) {
          setAuthToken(token);
          // setCookie('refreshToken', refreshToken); // optional, if you have a refresh token
          redirect({}, DEFAULT_PATH_AFTER_SIGN_IN, 'replace');
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const [requestOTPMutation] = useRequestOtpMutation({
    onCompleted: (data) => {
      if (!data) return;
      const otp_no = data?.RequestOTP?.status;
      if (otp_no) {
        toast.success(
          `An OTP is sent to ${phone}. Please enter the code within 2 minutes`
        );
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // ======================= EVENTS
  const handleLogin = (val: { user_mobile: string; password: string }) => {
    loginMutation({
      variables: {
        input: {
          user_mobile: '60' + val.user_mobile,
          password: val.password,
          device_id: getDeviceId(),
        },
      },
    });
  };

  const handleSecureLogin = (val: {
    user_mobile: string;
    password: string;
    otp_no: string;
  }) => {
    secureLoginMutation({
      variables: {
        input: {
          user_mobile: '60' + val.user_mobile,
          password: loginFormRef.current['form']?.getValues('password'),
          otp_no: val.otp_no,
          device_id: getDeviceId(),
        },
      },
    });
  };

  const handleOTPRequest = (val: string) => {
    setPhone('60' + val);
    requestOTPMutation({
      variables: {
        input: {
          user_mobile: '60' + val,
          otp_type: 'Login',
        },
      },
    });
  };

  // ======================= RENDER FUNCTIONS
  const renderAgreementCheckbox = () => {
    return (
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
          }
          label={
            <Typography variant={'body2'}>
              {`I agree to WeSettle's `}
              <Link
                target="_blank"
                href={'https://www.wesettle.my/term-and-conditions'}
                component={NextLink}
              >
                Terms and Conditions
              </Link>
              {' and '}
              <Link
                target="_blank"
                href={'https://www.wesettle.my/privacy-policy'}
                component={NextLink}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
      </Box>
    );
  };

  const renderFooter = () => {
    return (
      <>
        <Box
          sx={{
            mb: isOtpLogin ? 8 : 11,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Link
            sx={{ mt: 2, fontWeight: 500 }}
            variant={'body2'}
            href={'/forgot-password'}
          >
            Forgot Password?
          </Link>
        </Box>
        {isOtpLogin && (
          <Typography className={'text-[1rem] mb-4'} color={'error'}>
            {`This device isn't recognized. For your security, please proceed with OTP login with phone number.`}
          </Typography>
        )}
        {renderAgreementCheckbox()}
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={!agree || loginLoading || secureLoginLoading}
        >
          Login
        </Button>
      </>
    );
  };

  // ======================= VIEWS
  return (
    <FormRenderer
      ref={loginFormRef}
      fieldContainerProps={{
        position: 'relative',
        gap: 4,
        rowGap: 0,
      }}
      formOptions={{
        resolver: yupResolver(isOtpLogin ? secureLoginSchema : loginSchema),
      }}
      schema={getLoginSchema({ isOtpLogin, getOTP: handleOTPRequest })}
      footer={renderFooter()}
      onSubmit={isOtpLogin ? handleSecureLogin : handleLogin}
    />
  );
};

export default LoginContainer;
